import { useEffect, useRef, useState, type ReactNode } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import type { ConnectSuccess } from '@erikdakoda/connection/api-handlers/ConnectHandler';
import type { ReconnectSuccess } from '@erikdakoda/connection/api-handlers/ConnectionReconnectHandler';
import type { RefreshSuccess } from '@erikdakoda/connection/api-handlers/ConnectionRefreshHandler';
import type {
  ListConnectableToolkitsResult,
  ToolkitConnectOptions,
  ToolkitConnectScheme,
} from '@erikdakoda/connection/shared/toolkitCatalogTypes';
import { buildUniqueConnectionName } from '@erikdakoda/connection/shared/buildUniqueConnectionName';
import { useFindManyComposioConnection } from '@erikdakoda/database/hooks/composio-connection';
import { useFindManySpaceUser } from '@erikdakoda/database/hooks/space-user';
import type { ToolAccessMode, ConnectionOwnerScope } from '@erikdakoda/database/enums';
import DialogBox from '@erikdakoda/ui/components/DialogBox';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import ScrollBox from '@erikdakoda/ui/components/ScrollBox';
import SearchBox from '@erikdakoda/ui/components/SearchBox';
import Spinner from '@erikdakoda/ui/components/Spinner';
import SpinnerOverlay from '@erikdakoda/ui/components/SpinnerOverlay';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { doFetch } from '@erikdakoda/ui/shared/doFetch';

import {
  CONNECTION_BROWSER_FINISHED_EVENT,
  CONNECTION_CONNECTED_MESSAGE_TYPE,
  launchConnect,
} from '../shared/launchConnect';
import ConnectionCard from './ConnectionCard';
import ToolkitLogo from './ToolkitLogo';

const OAUTH_POLL_INTERVAL_MS = 3000;
const SEARCH_DEBOUNCE_MS = 300;
const TOOLKIT_PAGE_LIMIT = 24;
/** Composio rejects `search` shorter than this; shorter queries use the unfiltered list. */
const MIN_SEARCH_LENGTH = 3;
/** Select value for personal (USER-scope) connections. */
const SHARE_JUST_ME = 'USER';

type DialogStep = 'choose' | 'authenticate' | 'confirm';

type ConnectOrReconnectResponse = ConnectSuccess | ReconnectSuccess;

type ListedToolkitItem = ListConnectableToolkitsResult['items'][number];

type ShareTarget = typeof SHARE_JUST_ME | string;

export type AddConnectionDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConnected: (connectionId: string) => void;
  readonly initialToolkitSlug?: string;
  /** Forces (or reconnects) a SERVICE-scope connection. ADMIN/TEAM only. */
  readonly forService?: boolean;
  /** When set, re-authorizes an existing connection instead of creating a new one. */
  readonly reconnectConnectionId?: string;
  /** Pre-selects USER or SPACE on the authenticate step (ignored when `forService`). */
  readonly initialScope?: 'USER' | 'SPACE';
};

/**
 * Shared three-step dialog for connecting (or reconnecting) a Composio toolkit: choose from the
 * live catalog, authenticate via OAuth or API key (with connection name), then confirm.
 */
export default function AddConnectionDialog({
  open,
  onClose,
  onConnected,
  initialToolkitSlug,
  forService = false,
  reconnectConnectionId,
  initialScope = 'USER',
}: AddConnectionDialogProps) {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const { user } = useCurrentUser();
  const exception = useException();
  // keep: useException returns a new function identity each render; effects must not depend on it.
  const exceptionRef = useRef(exception);
  exceptionRef.current = exception;

  const userId = user?.id ?? null;
  const activeSpaceId = user?.activeSpaceId ?? null;

  const defaultShareTarget = (): ShareTarget => {
    if (forService) return SHARE_JUST_ME;
    if (initialScope === 'SPACE' && activeSpaceId) return activeSpaceId;
    return SHARE_JUST_ME;
  };

  const [step, setStep] = useState<DialogStep>(initialToolkitSlug ? 'authenticate' : 'choose');
  const [searchFilter, setSearchFilter] = useState('');
  const [toolkits, setToolkits] = useState<readonly ListedToolkitItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingToolkits, setIsLoadingToolkits] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedToolkitSlug, setSelectedToolkitSlug] = useState<string | null>(
    initialToolkitSlug ?? null,
  );
  const [connectOptions, setConnectOptions] = useState<ToolkitConnectOptions | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState<ToolkitConnectScheme | null>(null);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [shareTarget, setShareTarget] = useState<ShareTarget>(defaultShareTarget);
  const [toolAccessMode, setToolAccessMode] = useState<ToolAccessMode>('READ_ONLY');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oauthPendingConnectionId, setOauthPendingConnectionId] = useState<string | null>(null);
  const [oauthFailed, setOauthFailed] = useState(false);
  const [connectedConnectionId, setConnectedConnectionId] = useState<string | null>(null);

  const listScope: ConnectionOwnerScope = forService
    ? 'SERVICE'
    : shareTarget === SHARE_JUST_ME
      ? 'USER'
      : 'SPACE';
  const selectedSpaceId = shareTarget === SHARE_JUST_ME ? null : shareTarget;

  const { data: memberSpaces } = useFindManySpaceUser(
    {
      where: { userId: userId ?? undefined },
      select: {
        spaceId: true,
        space: { select: { id: true, name: true } },
      },
      orderBy: { space: { name: 'asc' } },
    },
    { enabled: open && Boolean(userId) && !forService },
  );

  const { data: activeConnections } = useFindManyComposioConnection(
    {
      where: {
        scope: listScope,
        status: 'ACTIVE',
        trashedAt: null,
        ...(listScope === 'USER' && userId ? { ownerId: userId } : {}),
        ...(listScope === 'SPACE' && selectedSpaceId ? { spaceId: selectedSpaceId } : {}),
      },
      select: { toolkitSlug: true, name: true },
    },
    { enabled: open },
  );

  const activeCountByToolkit: Record<string, number> = {};
  const existingByToolkit: Record<string, string[]> = {};
  for (const row of activeConnections ?? []) {
    activeCountByToolkit[row.toolkitSlug] = (activeCountByToolkit[row.toolkitSlug] ?? 0) + 1;
    const existingName = row.name ?? row.toolkitSlug;
    existingByToolkit[row.toolkitSlug] = [
      ...(existingByToolkit[row.toolkitSlug] ?? []),
      existingName,
    ];
  }

  const toolkitDisplayName = connectOptions?.displayName ?? selectedToolkitSlug ?? '';
  const trimmedSearch = searchFilter.trim();
  const apiSearch = trimmedSearch.length >= MIN_SEARCH_LENGTH ? trimmedSearch : '';

  useEffect(() => {
    if (!open) return;
    setStep(initialToolkitSlug ? 'authenticate' : 'choose');
    setSearchFilter('');
    setToolkits([]);
    setNextCursor(null);
    setSelectedToolkitSlug(initialToolkitSlug ?? null);
    setConnectOptions(null);
    setSelectedScheme(null);
    setCredentialValues({});
    setName('');
    setToolAccessMode('READ_ONLY');
    setShareTarget(
      forService
        ? SHARE_JUST_ME
        : initialScope === 'SPACE' && activeSpaceId
          ? activeSpaceId
          : SHARE_JUST_ME,
    );
    setIsSubmitting(false);
    setOauthPendingConnectionId(null);
    setOauthFailed(false);
    setConnectedConnectionId(null);
  }, [open, initialToolkitSlug, initialScope, forService, activeSpaceId]);

  useEffect(() => {
    if (!open || step !== 'choose') return;
    let cancelled = false;
    setIsLoadingToolkits(true);
    void (async () => {
      try {
        const result = await doFetch<ListConnectableToolkitsResult>({
          url: '/api/connections/toolkits',
          method: 'GET',
          params: {
            ...(apiSearch ? { search: apiSearch } : {}),
            limit: String(TOOLKIT_PAGE_LIMIT),
          },
          exception: exceptionRef.current,
        });
        if (cancelled) return;
        setToolkits(result?.items ?? []);
        setNextCursor(result?.nextCursor ?? null);
      } finally {
        if (!cancelled) {
          setIsLoadingToolkits(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, step, apiSearch]);

  useEffect(() => {
    if (!open || !selectedToolkitSlug || step !== 'authenticate') return;
    let cancelled = false;
    setIsLoadingOptions(true);
    setConnectOptions(null);
    setSelectedScheme(null);
    void (async () => {
      try {
        const result = await doFetch<ToolkitConnectOptions>({
          url: `/api/connections/toolkits/${encodeURIComponent(selectedToolkitSlug)}`,
          method: 'GET',
          params: {},
          exception: exceptionRef.current,
        });
        if (cancelled) return;
        setConnectOptions(result ?? null);
        if (result && result.schemes.length > 0) {
          setSelectedScheme(result.schemes[0] ?? null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingOptions(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedToolkitSlug, step]);

  useEffect(() => {
    setCredentialValues({});
  }, [selectedScheme]);

  const existingNamesForSelectedToolkit = (activeConnections ?? [])
    .filter((row) => row.toolkitSlug === selectedToolkitSlug)
    .map((row) => row.name ?? '')
    .filter((value) => value.trim().length > 0);
  const existingNamesSignature = [...existingNamesForSelectedToolkit].sort().join('\u0000');

  useEffect(() => {
    if (!open || reconnectConnectionId || step !== 'authenticate') return;
    if (!selectedToolkitSlug || !toolkitDisplayName) return;
    const existing =
      existingNamesSignature.length > 0 ? existingNamesSignature.split('\u0000') : [];
    setName(buildUniqueConnectionName(toolkitDisplayName, existing));
  }, [
    open,
    reconnectConnectionId,
    step,
    selectedToolkitSlug,
    toolkitDisplayName,
    shareTarget,
    existingNamesSignature,
  ]);

  useEffect(() => {
    if (!oauthPendingConnectionId) return undefined;
    let cancelled = false;

    const checkStatus = async () => {
      const refreshed = await doFetch<RefreshSuccess>({
        url: `/api/connections/${oauthPendingConnectionId}/refresh`,
        method: 'POST',
        params: {},
        exception: exceptionRef.current,
      });
      if (cancelled || !refreshed) return;
      if (refreshed.status === 'ACTIVE') {
        setConnectedConnectionId(refreshed.connectionId);
        setOauthPendingConnectionId(null);
        setStep('confirm');
      } else if (
        refreshed.status === 'FAILED' ||
        refreshed.status === 'REVOKED' ||
        refreshed.status === 'EXPIRED'
      ) {
        setOauthFailed(true);
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; connectionId?: string } | null;
      if (
        data?.type === CONNECTION_CONNECTED_MESSAGE_TYPE &&
        data.connectionId === oauthPendingConnectionId
      ) {
        void checkStatus();
      }
    };
    const handleBrowserFinished = () => void checkStatus();

    window.addEventListener('message', handleMessage);
    window.addEventListener(CONNECTION_BROWSER_FINISHED_EVENT, handleBrowserFinished);
    const intervalId = window.setInterval(() => void checkStatus(), OAUTH_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('message', handleMessage);
      window.removeEventListener(CONNECTION_BROWSER_FINISHED_EVENT, handleBrowserFinished);
      window.clearInterval(intervalId);
    };
  }, [oauthPendingConnectionId]);

  const handleClose = (): void => {
    onClose();
  };

  const handleChooseToolkit = (toolkit: ListedToolkitItem): void => {
    setSelectedToolkitSlug(toolkit.slug);
    setStep('authenticate');
  };

  const handleLoadMore = async (): Promise<void> => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const result = await doFetch<ListConnectableToolkitsResult>({
        url: '/api/connections/toolkits',
        method: 'GET',
        params: {
          ...(apiSearch ? { search: apiSearch } : {}),
          cursor: nextCursor,
          limit: String(TOOLKIT_PAGE_LIMIT),
        },
        exception: exceptionRef.current,
      });
      if (!result) return;
      setToolkits((current) => {
        const seen = new Set(current.map((toolkit) => toolkit.slug));
        const appended = result.items.filter((toolkit) => {
          if (seen.has(toolkit.slug)) return false;
          seen.add(toolkit.slug);
          return true;
        });
        return [...current, ...appended];
      });
      setNextCursor(result.nextCursor);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const buildConnectUrl = (): string =>
    reconnectConnectionId
      ? `/api/connections/${reconnectConnectionId}/reconnect`
      : '/api/connections/connect';

  const buildCreateConnectParams = (computedName: string | undefined): Record<string, unknown> => {
    const base = {
      toolkitSlug: selectedToolkitSlug,
      toolkitName: toolkitDisplayName || undefined,
      authScheme: selectedScheme?.authScheme,
      name: computedName,
      forService,
      toolAccessMode,
    };
    if (forService) {
      return base;
    }
    if (shareTarget === SHARE_JUST_ME) {
      return { ...base, scope: 'USER' as const };
    }
    return { ...base, scope: 'SPACE' as const, spaceId: shareTarget };
  };

  const handleSubmitApiKey = async (): Promise<void> => {
    if (!selectedToolkitSlug || !selectedScheme) return;
    const computedName = reconnectConnectionId ? undefined : name.trim();
    if (!reconnectConnectionId && !computedName) return;
    setIsSubmitting(true);
    try {
      const result = await doFetch<ConnectOrReconnectResponse>({
        url: buildConnectUrl(),
        method: 'POST',
        params: reconnectConnectionId
          ? { credentials: credentialValues }
          : {
              ...buildCreateConnectParams(computedName),
              credentials: credentialValues,
            },
        exception: exceptionRef.current,
      });
      if (!result?.connectionId) return;
      setName(computedName ?? '');
      setConnectedConnectionId(result.connectionId);
      setStep('confirm');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOAuth = async (): Promise<void> => {
    if (!selectedToolkitSlug || !selectedScheme) return;
    const computedName = reconnectConnectionId ? undefined : name.trim();
    if (!reconnectConnectionId && !computedName) return;
    setIsSubmitting(true);
    setOauthFailed(false);
    try {
      const result = await doFetch<ConnectOrReconnectResponse>({
        url: buildConnectUrl(),
        method: 'POST',
        params: reconnectConnectionId ? {} : buildCreateConnectParams(computedName),
        exception: exceptionRef.current,
      });
      const connectionId = result?.connectionId ?? reconnectConnectionId ?? null;
      const redirectUrl = result?.redirectUrl;
      if (!connectionId || !redirectUrl) return;

      setName(computedName ?? '');
      await launchConnect(redirectUrl);
      setOauthPendingConnectionId(connectionId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinish = (): void => {
    if (connectedConnectionId) {
      onConnected(connectedConnectionId);
    }
    handleClose();
  };

  const handleBackToChoose = (): void => {
    setStep('choose');
    setOauthPendingConnectionId(null);
    setOauthFailed(false);
    setConnectOptions(null);
    setSelectedScheme(null);
  };

  const canGoBackToChoose = !initialToolkitSlug && !reconnectConnectionId;
  const nameRequired = !reconnectConnectionId;

  const renderChooseStep = (): ReactNode => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        gap: '1.25rem',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 360,
          mx: 'auto',
          pt: 2,
          alignSelf: 'center',
          flexShrink: 0,
        }}
      >
        <SearchBox
          key={open ? 'open' : 'closed'}
          defaultFilter=""
          setFilter={setSearchFilter}
          debounceMs={SEARCH_DEBOUNCE_MS}
          fullWidth
        />
      </Box>

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        {isLoadingToolkits && <SpinnerOverlay />}
        <ScrollBox overflowXHidden scrollTrackOutside>
          <Grid container spacing={2}>
            {toolkits.map((toolkit) => {
              const count = activeCountByToolkit[toolkit.slug] ?? 0;
              return (
                <Grid key={toolkit.slug} size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
                  <ConnectionCard
                    variant="toolkit"
                    toolkit={toolkit}
                    connectedCount={count}
                    onSelect={() => handleChooseToolkit(toolkit)}
                  />
                </Grid>
              );
            })}
          </Grid>
          {toolkits.length === 0 && !isLoadingToolkits && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('noToolkitsFound')}
            </Typography>
          )}
          {nextCursor && (
            <Box sx={{ textAlign: 'center', pt: 2 }}>
              <Button onClick={handleLoadMore} loading={isLoadingMore}>
                {t('loadMoreToolkits')}
              </Button>
            </Box>
          )}
        </ScrollBox>
      </Box>
    </Box>
  );

  const renderSchemeChoice = (): ReactNode => {
    if (!connectOptions || connectOptions.schemes.length <= 1) return null;
    return (
      <ToggleButtonGroup
        exclusive
        color="primary"
        size="small"
        value={selectedScheme?.authScheme ?? null}
        onChange={(_event, value: string | null) => {
          if (!value) return;
          const scheme = connectOptions.schemes.find((candidate) => candidate.authScheme === value);
          setSelectedScheme(scheme ?? null);
        }}
        aria-label={t('authSchemeChoice')}
      >
        {connectOptions.schemes.map((scheme) => (
          <ToggleButton key={scheme.authScheme} value={scheme.authScheme}>
            {t(scheme.labelKey, { name: toolkitDisplayName })}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  const renderScopeChoice = (): ReactNode => {
    if (forService || reconnectConnectionId) return null;
    return (
      <Stack spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
        <TextField
          select
          label={t('scopeChoice')}
          value={shareTarget}
          onChange={(event) => setShareTarget(event.target.value)}
          sx={{ textAlign: 'left', minWidth: 220 }}
        >
          <MenuItem value={SHARE_JUST_ME}>{t('scopeJustMe')}</MenuItem>
          {(memberSpaces ?? []).map((membership) => (
            <MenuItem key={membership.spaceId} value={membership.spaceId}>
              {t('scopeEveryoneInSpaceNamed', {
                name: membership.space.name,
              })}
            </MenuItem>
          ))}
        </TextField>
        {shareTarget !== SHARE_JUST_ME && (
          <Alert severity="warning">{t('scopePermanentWarning')}</Alert>
        )}
      </Stack>
    );
  };

  const renderAccessModeChoice = (): ReactNode => {
    if (reconnectConnectionId) return null;
    return (
      <Stack spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
        <ToggleButtonGroup
          exclusive
          color="primary"
          size="small"
          value={toolAccessMode}
          onChange={(_event, value: ToolAccessMode | null) => {
            if (value === 'READ_ONLY' || value === 'READ_WRITE') {
              setToolAccessMode(value);
            }
          }}
          aria-label={t('toolAccessModeChoice')}
        >
          <ToggleButton value="READ_ONLY">
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <FaIconUvilo icon="pencil-slash" />
              <span>{t('toolAccessModeReadOnly')}</span>
            </Stack>
          </ToggleButton>
          <ToggleButton value="READ_WRITE">
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <FaIconUvilo icon="pencil" />
              <span>{t('toolAccessModeReadWrite')}</span>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {t(
            toolAccessMode === 'READ_WRITE'
              ? 'toolAccessModeChoiceHintReadWrite'
              : 'toolAccessModeChoiceHintReadOnly',
          )}
        </Typography>
      </Stack>
    );
  };

  const renderExistingAccounts = (): ReactNode => {
    if (!selectedToolkitSlug) return null;
    const existing = existingByToolkit[selectedToolkitSlug] ?? [];
    if (existing.length === 0) return null;
    return (
      <Alert severity="warning">
        {t('existingConnectionsHint', { names: existing.join(', ') })}
      </Alert>
    );
  };

  const renderAuthenticateStep = (): ReactNode => {
    if (!selectedToolkitSlug) return null;
    if (isLoadingOptions) {
      return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Spinner />
        </Box>
      );
    }
    if (!connectOptions) {
      return <Alert severity="error">{t('toolkitNotConnectable')}</Alert>;
    }

    const textFieldSx = { mt: 4, width: '100%' } as const;

    return (
      <Stack spacing={4} sx={{ alignItems: 'center', textAlign: 'center', width: '100%' }}>
        <Box sx={{ width: '100%', pb: 4 }}>
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <ToolkitLogo toolkitSlug={connectOptions.slug} size={56} alt="" />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {toolkitDisplayName}
            </Typography>
          </Stack>
        </Box>

        {renderExistingAccounts()}
        {renderScopeChoice()}
        {renderAccessModeChoice()}
        {renderSchemeChoice()}

        {!reconnectConnectionId && (
          <Box sx={{ width: '100%', pt: 4 }}>
            <TextField
              fullWidth
              required
              label={t('nameRequired')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              sx={{ width: '100%' }}
            />
          </Box>
        )}

        {selectedScheme?.kind === 'api_key' && (
          <Stack spacing={0} sx={{ width: '100%', alignItems: 'center' }}>
            {selectedScheme.fields.map((field) => (
              <TextField
                key={field.name}
                fullWidth
                required={field.required}
                type={field.secret ? 'password' : 'text'}
                label={field.displayName}
                helperText={field.description || undefined}
                value={credentialValues[field.name] ?? ''}
                onChange={(event) =>
                  setCredentialValues((current) => ({
                    ...current,
                    [field.name]: event.target.value,
                  }))
                }
                sx={textFieldSx}
              />
            ))}
            <Button
              variant="contained"
              loading={isSubmitting}
              disabled={
                (nameRequired && !name.trim()) ||
                selectedScheme.fields.some(
                  (field) => field.required && !credentialValues[field.name]?.trim(),
                )
              }
              onClick={handleSubmitApiKey}
              sx={{ mt: 4 }}
            >
              {t('connectButton')}
            </Button>
          </Stack>
        )}

        {selectedScheme?.kind === 'oauth' &&
          (oauthPendingConnectionId ? (
            <Box sx={{ width: '100%', pt: 4 }}>
              <Stack spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: 'center', justifyContent: 'center' }}
                >
                  <Spinner width={20} />
                  <Typography variant="body2" color="text.secondary">
                    {t('oauthWaitingDescription')}
                  </Typography>
                </Stack>
                {oauthFailed && <Alert severity="warning">{t('oauthFailedDescription')}</Alert>}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ pt: 4 }}>
              <Button
                variant="contained"
                loading={isSubmitting}
                disabled={nameRequired && !name.trim()}
                onClick={handleStartOAuth}
              >
                {t(selectedScheme.labelKey, { name: toolkitDisplayName })}
              </Button>
            </Box>
          ))}
      </Stack>
    );
  };

  const renderConfirmStep = (): ReactNode => (
    <Stack spacing={4} sx={{ alignItems: 'center', textAlign: 'center', py: 4 }}>
      <FaIconUvilo
        icon="circle-check"
        prefix="fat"
        square
        color="success.main"
        sx={{ width: '4rem', height: '4rem' }}
      />
      <Typography variant="body2" color="text.secondary">
        {t('connectionSuccessDescription', { name: toolkitDisplayName })}
      </Typography>
      <Button variant="contained" onClick={handleFinish}>
        {t('finish')}
      </Button>
    </Stack>
  );

  const isCatalogStep = step === 'choose';

  const dialogTitle =
    step === 'choose'
      ? t('chooseToolkitTitle')
      : step === 'authenticate'
        ? t('connectTitle')
        : t('confirmTitle');

  return (
    <DialogBox
      className="add-connection-dialog"
      isOpen={open}
      title={dialogTitle}
      onConfirm={handleClose}
      onCancel={handleClose}
      hideConfirm
      hideCancel
      showClose
      closeTitle={t('cancel')}
      showBack={canGoBackToChoose && step === 'authenticate'}
      backTitle={t('back')}
      onBack={handleBackToChoose}
      maxWidth={isCatalogStep ? 'md' : 'sm'}
      fullWidth
      contentDividers={false}
      sx={(theme) => ({
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.up('sm')]: {
            ...(isCatalogStep
              ? {
                  height: 'calc(100% - 64px)',
                  maxHeight: 'calc(100% - 64px)',
                }
              : {
                  maxHeight: 'calc(100% - 64px)',
                }),
          },
        },
        '& .MuiDialogContent-root': isCatalogStep
          ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : {
              overflow: 'auto',
            },
      })}
    >
      {step === 'choose' && renderChooseStep()}
      {step === 'authenticate' && renderAuthenticateStep()}
      {step === 'confirm' && renderConfirmStep()}
    </DialogBox>
  );
}
